import { create } from "zustand";
import type {
  FormPage,
  FormSection,
  FormQuestion,
  RepeatingGroupConfig,
  QuestionType,
} from "@/components/features/FormBuilder/types/form.types";
import {
  getFieldType,
  GROUP_CHILD_EXCLUDED_TYPES,
} from "@/components/features/FormBuilder/data/fieldTypes";
import { isContextTrigger } from "@/components/features/FormBuilder/data/contextTriggers";

export function generateId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function toQuestionType(rawType: unknown, fallback: QuestionType): QuestionType {
  return typeof rawType === "string" && rawType.length > 0 ? (rawType as QuestionType) : fallback;
}

// ─── Question scoping ──────────────────────────────────────
// A question array can either be a section's top-level questions, or the
// `fields` of a repeating-group question living inside that section.

export interface QuestionScope {
  pageId: string;
  sectionId: string;
  groupId?: string;
}

function getScopeArray(pages: FormPage[], scope: QuestionScope): FormQuestion[] | null {
  const page = pages.find((p) => p.id === scope.pageId);
  const section = page?.sections.find((s) => s.id === scope.sectionId);
  if (!section) return null;
  if (!scope.groupId) return section.questions;
  const groupQuestion = section.questions.find((q) => q.id === scope.groupId);
  return groupQuestion?.fields ?? null;
}

function setScopeArray(
  pages: FormPage[],
  scope: QuestionScope,
  updater: (questions: FormQuestion[]) => FormQuestion[],
): FormPage[] {
  return pages.map((pg) => {
    if (pg.id !== scope.pageId) return pg;
    return {
      ...pg,
      sections: pg.sections.map((sec) => {
        if (sec.id !== scope.sectionId) return sec;
        if (!scope.groupId) {
          return { ...sec, questions: updater(sec.questions) };
        }
        return {
          ...sec,
          questions: sec.questions.map((q) =>
            q.id === scope.groupId ? { ...q, fields: updater(q.fields ?? []) } : q,
          ),
        };
      }),
    };
  });
}

// ─── Reference remapping (duplication) ─────────────────────
// A repeating group's children can only target siblings within the same `fields` array,
// and a group's rowTitle/rowSubtitle can embed `{childId}` tokens. Both must be
// rewritten when the group (or its containing section/page) is cloned.

function remapGroupConfigTokens(
  text: string | undefined,
  idMap: Record<string, string>,
): string | undefined {
  if (!text) return text;
  return text.replace(/\{([^}]+)\}/g, (match, token) =>
    idMap[token] ? `{${idMap[token]}}` : match,
  );
}

function remapGroupChildren(fields: FormQuestion[], idMap: Record<string, string>): FormQuestion[] {
  return fields.map((f) => ({
    ...f,
    id: idMap[f.id] ?? f.id,
    conditionalLogic: f.conditionalLogic?.map((rule) => ({
      ...rule,
      triggerQuestionId: idMap[rule.triggerQuestionId] ?? rule.triggerQuestionId,
      targetQuestionIds: rule.targetQuestionIds?.map((tid) => idMap[tid] ?? tid),
    })),
  }));
}

function remapGroupConfig(
  groupConfig: RepeatingGroupConfig,
  idMap: Record<string, string>,
): RepeatingGroupConfig {
  return {
    ...groupConfig,
    rowTitle: remapGroupConfigTokens(groupConfig.rowTitle, idMap),
    rowSubtitle: remapGroupConfigTokens(groupConfig.rowSubtitle, idMap),
  };
}

// Regenerates a repeating-group question's own children ids (and remaps every
// intra-group reference to them), without touching the group question's own id.
function regenerateGroupChildIds(q: FormQuestion): FormQuestion {
  if (q.type !== "repeating-group" || !q.fields?.length) return q;
  const idMap: Record<string, string> = {};
  q.fields.forEach((f) => {
    idMap[f.id] = generateId("q");
  });
  return {
    ...q,
    fields: remapGroupChildren(q.fields, idMap),
    groupConfig: q.groupConfig ? remapGroupConfig(q.groupConfig, idMap) : q.groupConfig,
  };
}

// Clones a top-level question with a fresh id, regenerating its group children
// (if any) too.
function cloneQuestion(q: FormQuestion): FormQuestion {
  const cloned = regenerateGroupChildIds(JSON.parse(JSON.stringify(q)));
  const id = generateId("q");
  const remap = (qid: string) => (qid === q.id ? id : qid);
  return {
    ...cloned,
    id,
    conditionalLogic: cloned.conditionalLogic?.map((rule) => ({
      ...rule,
      triggerQuestionId: remap(rule.triggerQuestionId),
      targetQuestionIds: rule.targetQuestionIds?.map(remap),
    })),
  };
}

// Clones a set of top-level questions (e.g. a section's or page's), giving
// each a fresh id, remapping conditionalLogic that references siblings within
// the same array, and regenerating any repeating group's own children ids.
function cloneQuestionsWithNewIds(questions: FormQuestion[]): FormQuestion[] {
  const idMap: Record<string, string> = {};
  questions.forEach((q) => {
    idMap[q.id] = generateId("q");
  });
  return questions.map((q) => {
    const withNewChildIds = regenerateGroupChildIds(q);
    return {
      ...withNewChildIds,
      id: idMap[q.id],
      conditionalLogic: withNewChildIds.conditionalLogic?.map((rule) => ({
        ...rule,
        triggerQuestionId: idMap[rule.triggerQuestionId] ?? rule.triggerQuestionId,
        targetQuestionIds: rule.targetQuestionIds?.map((tid) => idMap[tid] ?? tid),
      })),
    };
  });
}

// ─── Optimized JSON Serialization ─────────────────────────
// Strip default/empty values to reduce payload size by ~60-70%

function serializeQuestion(q: FormQuestion): Record<string, any> {
  const out: Record<string, any> = {
    id: q.id,
    type: q.type,
    question: q.question,
  };
  // Only include non-default values
  if (!q.required) out.required = false;
  else out.required = true;
  if (q.placeholder) out.placeholder = q.placeholder;
  if (q.description && q.description !== " " && q.description !== "")
    out.description = q.description;
  if (q.unit) out.unit = q.unit;
  if (q.options?.length) out.options = q.options;
  if (q.allowOther) out.allowOther = true;
  if (q.allowMultiple) out.allowMultiple = true;
  if (q.hidden) out.hidden = true;
  if (q.photoSlots?.length) out.photoSlots = q.photoSlots;
  if (q.videoSlots?.length) out.videoSlots = q.videoSlots;
  if (q.allowAdditionalPhotos) out.allowAdditionalPhotos = true;
  if (q.allowAdditionalVideos) out.allowAdditionalVideos = true;
  if (q.conditionalLogic?.length) {
    out.conditionalLogic = q.conditionalLogic.map((cl) => {
      const rule: Record<string, any> = {
        id: cl.id,
        condition: cl.condition,
        action: cl.action,
      };
      if (cl.triggerQuestionId && cl.triggerQuestionId !== q.id) {
        rule.triggerQuestionId = cl.triggerQuestionId;
      }
      if (cl.triggerValues?.length) rule.triggerValues = cl.triggerValues;
      if (cl.targetQuestionIds?.length) rule.targetQuestionIds = cl.targetQuestionIds;
      if (cl.targetSectionIds?.length) rule.targetSectionIds = cl.targetSectionIds;
      if (cl.targetPageIds?.length) rule.targetPageIds = cl.targetPageIds;
      return rule;
    });
    out.conditionalLogicEnabled = true;
  }
  if (q.groupConfig) {
    const lc: Record<string, any> = {};
    if (q.groupConfig.itemLabel && q.groupConfig.itemLabel !== "Entry")
      lc.itemLabel = q.groupConfig.itemLabel;
    if (q.groupConfig.addLabel && q.groupConfig.addLabel !== "Add another")
      lc.addLabel = q.groupConfig.addLabel;
    if (q.groupConfig.minItems !== undefined) lc.minItems = q.groupConfig.minItems;
    if (q.groupConfig.maxItems !== undefined) lc.maxItems = q.groupConfig.maxItems;
    if (q.groupConfig.rowTitle) lc.rowTitle = q.groupConfig.rowTitle;
    if (q.groupConfig.rowSubtitle) lc.rowSubtitle = q.groupConfig.rowSubtitle;
    out.groupConfig = lc;
  }
  if (q.fields?.length) out.fields = q.fields.map(serializeQuestion);
  return out;
}

function serializePages(pages: FormPage[]): Record<string, any>[] {
  return pages.map((p) => ({
    id: p.id,
    name: p.name,
    sections: p.sections.map((s) => ({
      id: s.id,
      name: s.name,
      ...(s.description ? { description: s.description } : {}),
      questions: s.questions.map(serializeQuestion),
    })),
  }));
}

// ─── Deserialize / Hydrate from JSON ──────────────────────
// Restore full FormQuestion shape from compact JSON

function hydrateQuestion(raw: Record<string, any>): FormQuestion {
  const type = toQuestionType(raw.type, "single-line-text");
  const ft = getFieldType(type);
  const question: FormQuestion = {
    id: raw.id || generateId("q"),
    type,
    typeName: ft?.label || raw.typeName || raw.type || "Single Line Text",
    question: raw.question || "",
    placeholder: raw.placeholder || "",
    required: raw.required !== false, // default true
    description: raw.description || "",
    unit: raw.unit || "",
    options: raw.options || undefined,
    allowOther: raw.allowOther || false,
    allowMultiple: raw.allowMultiple || false,
    hidden: raw.hidden || false,
    photoSlots: raw.photoSlots || (type === "photo-upload" ? [{ id: "1", label: "" }] : undefined),
    videoSlots: raw.videoSlots || (type === "video-upload" ? [{ id: "1", label: "" }] : undefined),
    allowAdditionalPhotos: raw.allowAdditionalPhotos || false,
    allowAdditionalVideos: raw.allowAdditionalVideos || false,
    conditionalLogic: (raw.conditionalLogic || []).map((cl: any) => ({
      id: cl.id || generateId("cl"),
      triggerQuestionId: cl.triggerQuestionId || raw.id,
      condition: cl.condition || "is",
      triggerValues: cl.triggerValues || [],
      action: cl.action || "show",
      targetQuestionIds: cl.targetQuestionIds || [],
      targetSectionIds: cl.targetSectionIds || [],
      targetPageIds: cl.targetPageIds || [],
    })),
    conditionalLogicEnabled:
      raw.conditionalLogicEnabled || raw.conditionalLogic?.length > 0 || false,
  };
  if (type === "repeating-group") {
    question.groupConfig = {
      itemLabel: raw.groupConfig?.itemLabel || "Entry",
      addLabel: raw.groupConfig?.addLabel || "Add another",
      minItems: raw.groupConfig?.minItems,
      maxItems: raw.groupConfig?.maxItems,
      rowTitle: raw.groupConfig?.rowTitle,
      rowSubtitle: raw.groupConfig?.rowSubtitle,
    };
    question.fields = (raw.fields || []).map(hydrateQuestion);
  }
  return question;
}

function hydratePages(raw: any[]): FormPage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [createDefaultPage()];
  return raw.map((p: any) => ({
    id: p.id || generateId("page"),
    name: p.name || "Untitled Page",
    sections: (p.sections || []).map((s: any) => ({
      id: s.id || generateId("sec"),
      name: s.name || "Untitled Section",
      description: s.description || "",
      questions: (s.questions || []).map(hydrateQuestion),
    })),
  }));
}

function createDefaultPage(): FormPage {
  return {
    id: "page-1",
    name: "Page 1",
    sections: [{ id: "sec-1", name: "Untitled Section", description: "", questions: [] }],
  };
}

// ─── Enforce child ordering ──────────────────────────────
// After any reorder, ensure conditional-logic children sit immediately after their parent.
// Supports multi-level nesting: grandchildren are placed after their parent (which is a child).
// Root questions (not children of anything) maintain their relative order.

function enforceChildOrdering(
  questions: FormQuestion[],
  getChildren: (qs: FormQuestion[], qid: string) => string[],
): FormQuestion[] {
  // Identify all child IDs and collect every parent for each child
  const allChildIds = new Set<string>();
  const childParents = new Map<string, string[]>();
  for (const q of questions) {
    const kids = getChildren(questions, q.id);
    for (const kid of kids) {
      allChildIds.add(kid);
      if (!childParents.has(kid)) childParents.set(kid, []);
      childParents.get(kid)!.push(q.id);
    }
  }

  // For each child, find its last parent in question order.
  // When a child has multiple parents (e.g. 3 acknowledgements all showing the same question),
  // it should be placed after the last parent so it appears below all of them.
  const lastParentOf = new Map<string, string>();
  for (const [childId, parentIds] of childParents) {
    const last = parentIds.reduce((latest, pid) => {
      return questions.findIndex((q) => q.id === pid) > questions.findIndex((q) => q.id === latest)
        ? pid
        : latest;
    });
    lastParentOf.set(childId, last);
  }

  const placed = new Set<string>();
  const result: FormQuestion[] = [];

  function placeWithChildren(q: FormQuestion) {
    if (placed.has(q.id)) return;
    result.push(q);
    placed.add(q.id);

    const childIds = getChildren(questions, q.id);
    const sortedChildIds = [...childIds]
      .filter((cid) => lastParentOf.get(cid) === q.id)
      .sort(
        (a, b) => questions.findIndex((x) => x.id === a) - questions.findIndex((x) => x.id === b),
      );
    for (const cid of sortedChildIds) {
      if (placed.has(cid)) continue;
      const child = questions.find((qq) => qq.id === cid);
      if (child) placeWithChildren(child);
    }
  }

  // Only start from root questions (not children of anything).
  for (const q of questions) {
    if (!allChildIds.has(q.id)) {
      placeWithChildren(q);
    }
  }

  // Safety: place any remaining questions that weren't reached
  // (e.g. orphaned children from deleted parents)
  for (const q of questions) {
    if (!placed.has(q.id)) {
      placeWithChildren(q);
    }
  }

  return result;
}

// ─── Store Interface ──────────────────────────────────────

interface FormStore {
  pages: FormPage[];
  currentPageIndex: number;
  selectedQuestionId: string | null;
  selectedSectionId: string | null;
  isPreview: boolean;
  previewPageIndex: number;

  // Init
  initFromJSON: (json: any[] | string) => void;

  // Setters
  setCurrentPageIndex: (idx: number) => void;
  setSelectedQuestionId: (id: string | null) => void;
  setSelectedSectionId: (id: string | null) => void;
  setIsPreview: (v: boolean) => void;
  setPreviewPageIndex: (idx: number) => void;

  // Page CRUD
  updatePage: (pageId: string, updates: Partial<FormPage>) => void;
  addPage: () => void;
  deletePage: (pageId: string) => void;
  clonePage: (pageId: string) => void;

  // Section CRUD
  addSection: (pageId: string) => void;
  updateSection: (pageId: string, sectionId: string, updates: Partial<FormSection>) => void;
  deleteSection: (pageId: string, sectionId: string) => void;
  duplicateSection: (pageId: string, sectionId: string) => void;

  // Question CRUD
  addQuestion: (scope: QuestionScope, fieldTypeId: string, atIndex?: number) => void;
  updateQuestion: (
    scope: QuestionScope,
    questionId: string,
    updates: Partial<FormQuestion>,
  ) => void;
  deleteQuestion: (scope: QuestionScope, questionId: string) => void;
  duplicateQuestion: (scope: QuestionScope, questionId: string) => void;
  moveQuestionGroup: (scope: QuestionScope, draggedId: string, overId: string) => void;
  moveChildGroup: (scope: QuestionScope, draggedId: string, overSiblingId: string) => void;
  getConditionalChildIds: (sectionQuestions: FormQuestion[], questionId: string) => string[];
  reorderOptions: (
    scope: QuestionScope,
    questionId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;

  // Helpers
  findQuestion: (questionId: string) => { question: FormQuestion; scope: QuestionScope } | null;
  getAllQuestions: () => (FormQuestion & { pageId: string; sectionId: string })[];
  getScopeQuestions: (scope: QuestionScope) => FormQuestion[];
  getExcludedTargetIds: (questionId: string, scope?: QuestionScope) => Set<string>;
  getFormJSON: () => string;
  getCompactJSON: () => string;
}

export const useFormStore = create<FormStore>()((set, get) => ({
  pages: [createDefaultPage()],
  currentPageIndex: 0,
  selectedQuestionId: null,
  selectedSectionId: null,
  isPreview: false,
  previewPageIndex: 0,

  // ── Init from JSON ──────────────────────────────────────
  initFromJSON: (json) => {
    try {
      const raw = typeof json === "string" ? JSON.parse(json) : json;
      const pages = hydratePages(raw);
      set({ pages, currentPageIndex: 0, selectedQuestionId: null });
    } catch (e) {
      console.error("Failed to parse form JSON:", e);
    }
  },

  // ── Setters ─────────────────────────────────────────────
  setCurrentPageIndex: (idx) => set({ currentPageIndex: idx }),
  setSelectedQuestionId: (id) => set({ selectedQuestionId: id }),
  setSelectedSectionId: (id) => set({ selectedSectionId: id }),
  setIsPreview: (v) => set({ isPreview: v }),
  setPreviewPageIndex: (idx) => set({ previewPageIndex: idx }),

  // ── Page CRUD ───────────────────────────────────────────
  updatePage: (pageId, updates) =>
    set((s) => ({ pages: s.pages.map((p) => (p.id === pageId ? { ...p, ...updates } : p)) })),

  addPage: () => {
    const id = generateId("page");
    set((s) => ({
      pages: [
        ...s.pages,
        {
          id,
          name: `Page ${s.pages.length + 1}`,
          sections: [
            {
              id: generateId("sec"),
              name: "Untitled Section",
              description: "",
              questions: [],
            },
          ],
        },
      ],
    }));
  },

  deletePage: (pageId) =>
    set((s) => ({
      pages: s.pages.length > 1 ? s.pages.filter((p) => p.id !== pageId) : s.pages,
    })),

  clonePage: (pageId) =>
    set((s) => {
      const idx = s.pages.findIndex((p) => p.id === pageId);
      if (idx < 0) return s;
      const clone: FormPage = JSON.parse(JSON.stringify(s.pages[idx]));
      clone.id = generateId("page");
      clone.name += " (Copy)";
      clone.sections.forEach((sec) => {
        sec.id = generateId("sec");
        sec.questions = cloneQuestionsWithNewIds(sec.questions);
      });
      const next = [...s.pages];
      next.splice(idx + 1, 0, clone);
      return { pages: next };
    }),

  // ── Section CRUD ────────────────────────────────────────
  addSection: (pageId) =>
    set((s) => ({
      pages: s.pages.map((pg) =>
        pg.id !== pageId
          ? pg
          : {
              ...pg,
              sections: [
                ...pg.sections,
                {
                  id: generateId("sec"),
                  name: `Section ${pg.sections.length + 1}`,
                  description: "",
                  questions: [],
                },
              ],
            },
      ),
    })),

  updateSection: (pageId, sectionId, updates) =>
    set((s) => ({
      pages: s.pages.map((pg) =>
        pg.id !== pageId
          ? pg
          : {
              ...pg,
              sections: pg.sections.map((sec) =>
                sec.id === sectionId ? { ...sec, ...updates } : sec,
              ),
            },
      ),
    })),

  deleteSection: (pageId, sectionId) =>
    set((s) => ({
      pages: s.pages.map((pg) => {
        if (pg.id !== pageId || pg.sections.length <= 1) return pg;
        return { ...pg, sections: pg.sections.filter((sec) => sec.id !== sectionId) };
      }),
    })),

  duplicateSection: (pageId, sectionId) =>
    set((s) => ({
      pages: s.pages.map((pg) => {
        if (pg.id !== pageId) return pg;
        const idx = pg.sections.findIndex((sec) => sec.id === sectionId);
        if (idx < 0) return pg;
        const clone: FormSection = JSON.parse(JSON.stringify(pg.sections[idx]));
        clone.id = generateId("sec");
        clone.name += " (Copy)";
        clone.questions = cloneQuestionsWithNewIds(clone.questions);
        const secs = [...pg.sections];
        secs.splice(idx + 1, 0, clone);
        return { ...pg, sections: secs };
      }),
    })),

  // ── Question CRUD ───────────────────────────────────────
  addQuestion: (scope, fieldTypeId, atIndex) => {
    const ft = getFieldType(fieldTypeId);
    if (!ft) return;
    if (scope.groupId && GROUP_CHILD_EXCLUDED_TYPES.includes(ft.id)) return;

    const isGroup = ft.id === "repeating-group";
    const q: FormQuestion = {
      id: generateId("question"),
      type: ft.id,
      typeName: ft.label,
      question: ft.id === "email" ? "Email Address" : "",
      placeholder: "",
      required: isGroup ? false : true,
      description: "",
      unit: "",
      allowOther: false,
      allowMultiple: false,
      hidden: false,
      options: ["radio", "checkbox", "dropdown"].includes(ft.id)
        ? ft.id === "radio"
          ? [
              { id: generateId("o"), value: "" },
              { id: generateId("o"), value: "" },
            ]
          : ft.id === "checkbox"
            ? [{ id: generateId("o"), value: "" }]
            : [
                { id: generateId("o"), value: "Option 1" },
                { id: generateId("o"), value: "Option 2" },
              ]
        : undefined,
      photoSlots: ft.id === "photo-upload" ? [{ id: "1", label: "" }] : undefined,
      videoSlots: ft.id === "video-upload" ? [{ id: "1", label: "" }] : undefined,
      conditionalLogic: [],
      conditionalLogicEnabled: false,
      ...(isGroup
        ? {
            groupConfig: { itemLabel: "Entry", addLabel: "Add another" },
            fields: [],
          }
        : {}),
    };
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => {
        const next = [...qs];
        if (atIndex !== undefined) next.splice(atIndex, 0, q);
        else next.push(q);
        return next;
      }),
      selectedQuestionId: q.id,
    }));
  },

  updateQuestion: (scope, questionId, updates) => {
    const hasLogicChange = "conditionalLogic" in updates || "conditionalLogicEnabled" in updates;
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => {
        const updatedQs = qs.map((q) => (q.id === questionId ? { ...q, ...updates } : q));
        if (hasLogicChange) {
          return enforceChildOrdering(updatedQs, get().getConditionalChildIds);
        }
        return updatedQs;
      }),
    }));
  },

  deleteQuestion: (scope, questionId) =>
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => qs.filter((q) => q.id !== questionId)),
      selectedQuestionId: s.selectedQuestionId === questionId ? null : s.selectedQuestionId,
    })),

  duplicateQuestion: (scope, questionId) =>
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => {
        const idx = qs.findIndex((q) => q.id === questionId);
        if (idx < 0) return qs;
        const clone = cloneQuestion(qs[idx]);
        const next = [...qs];
        next.splice(idx + 1, 0, clone);
        return next;
      }),
    })),

  moveQuestionGroup: (scope, draggedId, overId) =>
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => {
        const getChildren = get().getConditionalChildIds;
        const dragChildren = getChildren(qs, draggedId);
        const groupIds = new Set([draggedId, ...dragChildren]);

        const group = qs.filter((q) => groupIds.has(q.id));
        const rest = qs.filter((q) => !groupIds.has(q.id));

        let overTarget = overId;
        if (groupIds.has(overId)) return qs;

        const overParent = rest.find((q) => {
          const kids = getChildren(qs, q.id);
          return kids.includes(overTarget);
        });
        if (overParent) overTarget = overParent.id;

        const overIdx = rest.findIndex((q) => q.id === overTarget);
        if (overIdx < 0) return qs;

        const dragOldIdx = qs.findIndex((q) => q.id === draggedId);
        const overOldIdx = qs.findIndex((q) => q.id === overTarget);
        const insertIdx = dragOldIdx < overOldIdx ? overIdx + 1 : overIdx;

        const newQs = [...rest];
        newQs.splice(insertIdx, 0, ...group);

        return enforceChildOrdering(newQs, getChildren);
      }),
    })),

  moveChildGroup: (scope, draggedId, overSiblingId) =>
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) => {
        const getChildren = get().getConditionalChildIds;

        const collectSubtree = (qid: string): string[] => {
          const kids = getChildren(qs, qid);
          return [qid, ...kids.flatMap(collectSubtree)];
        };

        const groupIds = new Set(collectSubtree(draggedId));
        const group = qs.filter((q) => groupIds.has(q.id));
        const rest = qs.filter((q) => !groupIds.has(q.id));

        const overIdx = rest.findIndex((q) => q.id === overSiblingId);
        if (overIdx < 0) return qs;

        const dragOldIdx = qs.findIndex((q) => q.id === draggedId);
        const overOldIdx = qs.findIndex((q) => q.id === overSiblingId);
        const insertIdx = dragOldIdx < overOldIdx ? overIdx + 1 : overIdx;

        const newQs = [...rest];
        newQs.splice(insertIdx, 0, ...group);
        return newQs;
      }),
    })),

  getConditionalChildIds: (sectionQuestions, questionId) => {
    const parent = sectionQuestions.find((q) => q.id === questionId);
    if (!parent?.conditionalLogicEnabled || !parent.conditionalLogic?.length) return [];
    const childIds = new Set<string>();
    const sectionQIds = new Set(sectionQuestions.map((q) => q.id));
    for (const rule of parent.conditionalLogic) {
      // A context rule depends on who fills the form, not on this question's answer,
      // so its targets are not this question's conditional children.
      if (isContextTrigger(rule.triggerQuestionId)) continue;
      if ((rule.action === "show" || rule.action === "hide") && rule.targetQuestionIds?.length) {
        for (const tid of rule.targetQuestionIds) {
          if (tid !== questionId && sectionQIds.has(tid)) childIds.add(tid);
        }
      }
    }
    return Array.from(childIds);
  },

  reorderOptions: (scope, questionId, fromIndex, toIndex) =>
    set((s) => ({
      pages: setScopeArray(s.pages, scope, (qs) =>
        qs.map((q) => {
          if (q.id !== questionId || !q.options) return q;
          const opts = [...q.options];
          const [item] = opts.splice(fromIndex, 1);
          opts.splice(toIndex, 0, item);
          return { ...q, options: opts };
        }),
      ),
    })),

  // ── Helpers ─────────────────────────────────────────────
  findQuestion: (questionId) => {
    const { pages } = get();
    for (const p of pages) {
      for (const s of p.sections) {
        const q = s.questions.find((qq) => qq.id === questionId);
        if (q) return { question: q, scope: { pageId: p.id, sectionId: s.id } };
        for (const parent of s.questions) {
          if (parent.type !== "repeating-group" || !parent.fields) continue;
          const child = parent.fields.find((f) => f.id === questionId);
          if (child) {
            return {
              question: child,
              scope: { pageId: p.id, sectionId: s.id, groupId: parent.id },
            };
          }
        }
      }
    }
    return null;
  },

  getAllQuestions: () => {
    const { pages } = get();
    const all: (FormQuestion & { pageId: string; sectionId: string })[] = [];
    pages.forEach((p) =>
      p.sections.forEach((s) =>
        s.questions.forEach((q) => all.push({ ...q, pageId: p.id, sectionId: s.id })),
      ),
    );
    return all;
  },

  getScopeQuestions: (scope) => getScopeArray(get().pages, scope) ?? [],

  // Returns all question IDs that should NOT be selectable as targets
  // for the given question's conditional logic (prevents circular references).
  // Excludes: the question itself, all its ancestors, and all its descendants.
  // With no scope, this searches across the whole form (top-level behavior);
  // with a scope, it is restricted to that scope's own question array (e.g. a
  // group's fields, which can only target their own siblings).
  getExcludedTargetIds: (questionId, scope) => {
    const getChildren = get().getConditionalChildIds;
    const excluded = new Set<string>([questionId]);

    let allQuestions: FormQuestion[];
    if (scope) {
      allQuestions = getScopeArray(get().pages, scope) ?? [];
    } else {
      const { pages } = get();
      allQuestions = [];
      pages.forEach((p) =>
        p.sections.forEach((s) => s.questions.forEach((q) => allQuestions.push(q))),
      );
    }

    // Build parent→children and child→parent maps
    const parentOf: Record<string, string> = {};
    for (const q of allQuestions) {
      const kids = getChildren(allQuestions, q.id);
      for (const kid of kids) {
        parentOf[kid] = q.id;
      }
    }

    // Walk up: exclude all ancestors
    let current = questionId;
    while (parentOf[current] && !excluded.has(parentOf[current])) {
      excluded.add(parentOf[current]);
      current = parentOf[current];
    }

    // Walk down: exclude all descendants (recursive)
    const visited = new Set<string>();
    function excludeDescendants(qid: string) {
      if (visited.has(qid)) return;
      visited.add(qid);
      const kids = getChildren(allQuestions, qid);
      for (const kid of kids) {
        excluded.add(kid);
        excludeDescendants(kid);
      }
    }
    excludeDescendants(questionId);

    return excluded;
  },

  // Full JSON (for debugging/internal use)
  getFormJSON: () => JSON.stringify(get().pages, null, 2),

  // Compact JSON (optimized for API payloads — ~60-70% smaller)
  getCompactJSON: () => JSON.stringify(serializePages(get().pages)),
}));
